const BASE_DETECTION_REGEX = `\\[oboku\\~[^\\]]*\\]`

/**
 * Will extract any oboku normalized metadata that exist in the resource id string.
 * Use this method to enrich the content that is being synchronized
 * @example
 * "foo [oboku~no_collection]" -> { isCollection: false }
 * "foo [oboku~tags~bar]" -> { tags: ['bar'] }
 * "foo [oboku~tags~bar,bar2]" -> { tags: ['bar', 'bar2'] }
 */
export const extractDirectivesFromName = (
  name: string,
): {
  isNotACollection: boolean
  tags: string[]
  isIgnored: boolean
  direction: "rtl" | "ltr" | undefined
  isbn?: string | undefined
  series: boolean | undefined
  year?: string | undefined
  ignoreMetadataFile: boolean
  ignoreMetadataSources: boolean
  isWebtoon: boolean
  metadataTitle?: string | undefined
  googleVolumeId?: string | undefined
  metadataSourceOnly?: string | undefined
} => {
  let isNotACollection = false
  let tags: string[] = []
  let isIgnored = false
  let direction: "rtl" | "ltr" | undefined
  let year: string | undefined
  let isbn: string | undefined
  let series: boolean | undefined
  let ignoreMetadataFile = false
  let ignoreMetadataSources = false
  let metadataTitle: string | undefined
  let isWebtoon = false
  let googleVolumeId: string | undefined
  let metadataSourceOnly: string | undefined

  const directives = name
    .match(/(\[oboku~[^\]]*\])+/gi)
    ?.map((str) => str.replace(/\[oboku~/, "").replace(/\]/, ""))

  directives?.forEach((directive) => {
    if (directive === "no_collection") {
      isNotACollection = true
    }

    if (directive === "webtoon") {
      isWebtoon = true
    }

    if (directive.startsWith("google-volume-id~")) {
      const value = directive.replace(/google-volume-id~/, "")
      googleVolumeId = value
    }

    if (directive.startsWith("metadata-ignore-file")) {
      ignoreMetadataFile = true
    }

    if (directive.startsWith("metadata-ignore-sources")) {
      ignoreMetadataSources = true
    }

    if (directive.startsWith("metadata-title~")) {
      const value = directive.replace(/metadata-title~/, "")
      metadataTitle = value
    }

    if (directive.startsWith("metadata-source-")) {
      // Extract the pattern metadata-source-{foo}-only
      const match = directive.match(/^metadata-source-(.+)-only$/)
      if (match) {
        metadataSourceOnly = match[1] // This captures the dynamic 'foo' part
      }
    }

    if (directive === "ignore") {
      isIgnored = true
    }

    if (directive === "series") {
      series = true
    }

    if (directive.startsWith("year~")) {
      const value = directive.replace(/year~/, "")
      year = value
    }

    if (directive.startsWith("direction~")) {
      const value = directive.replace(/direction~/, "")
      if (value === "ltr" || value === "rtl") {
        direction = value
      }
    }

    if (directive.startsWith("isbn~")) {
      const value = directive.replace(/isbn~/, "")
      isbn = value
    }

    if (directive.startsWith("tags~")) {
      const newTags: string[] | undefined = directive
        // biome-ignore lint/complexity/noUselessEscapeInRegex: TODO
        .replace(/tags\~/, "")
        .split(",")
      tags = [...tags, ...(newTags || [])]
    }
  })

  return {
    series,
    isNotACollection,
    tags,
    isIgnored,
    direction,
    isbn,
    year,
    ignoreMetadataFile,
    ignoreMetadataSources,
    isWebtoon,
    metadataTitle,
    googleVolumeId,
    metadataSourceOnly,
  }
}

export const removeDirectiveFromString = (str: string) =>
  str
    .replace(new RegExp(`( ${BASE_DETECTION_REGEX})+`, "ig"), "")
    .replace(new RegExp(`(${BASE_DETECTION_REGEX} )+`, "ig"), "")
    .replace(new RegExp(`(${BASE_DETECTION_REGEX})+`, "ig"), "")
